import { useEffect, useState, useCallback } from "react";
import {
  doc,
  setDoc,
  onSnapshot,
  collection,
  query,
  where,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useAuth } from "@/contexts/AuthContext";

export type AgentStatus = "available" | "busy" | "away" | "offline";

export type AgentPresence = {
  userId: string;
  status: AgentStatus;
  displayName?: string;
  role?: string;
  lastSeen?: string;
};

export function useAgentPresence() {
  const { user } = useAuth();
  const [agents, setAgents] = useState<AgentPresence[]>([]);
  const [loading, setLoading] = useState(true);

  const setPresence = useCallback(
    async (status: AgentStatus) => {
      if (!user?.id) return;
      try {
        await setDoc(
          doc(db, "userAvailability", user.id),
          {
            userId: user.id,
            status,
            displayName: user.displayName || user.email || "",
            role: user.roles?.[0] || "",
            lastSeen: new Date().toISOString(),
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
      } catch (err) {
        console.error("[useAgentPresence] setPresence error:", err);
      }
    },
    [user?.id]
  );

  useEffect(() => {
    if (!user?.id) return;

    setPresence("available");

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        setPresence("away");
      } else {
        setPresence("available");
      }
    };

    const handleBeforeUnload = () => {
      setPresence("offline");
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("beforeunload", handleBeforeUnload);
      setPresence("offline");
    };
  }, [user?.id, setPresence]);

  useEffect(() => {
    const q = query(
      collection(db, "userAvailability"),
      where("status", "in", ["available", "busy", "away"])
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const presenceList: AgentPresence[] = snapshot.docs.map((d) => ({
          userId: d.id,
          status: d.data().status as AgentStatus,
          displayName: d.data().displayName || "",
          role: d.data().role || "",
          lastSeen: d.data().lastSeen || "",
        }));
        setAgents(presenceList);
        setLoading(false);
      },
      (err) => {
        console.error("[useAgentPresence] snapshot error:", err);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  const availableAgents = agents.filter((a) => a.status === "available");
  const busyAgents = agents.filter((a) => a.status === "busy");
  const awayAgents = agents.filter((a) => a.status === "away");

  return {
    agents,
    availableAgents,
    busyAgents,
    awayAgents,
    loading,
    setPresence,
    isAnyAgentAvailable: availableAgents.length > 0,
  };
}

export function useUserPresence(userId: string | undefined) {
  const [presence, setPresence] = useState<AgentPresence | null>(null);

  useEffect(() => {
    if (!userId) return;

    const unsubscribe = onSnapshot(
      doc(db, "userAvailability", userId),
      (snap) => {
        if (snap.exists()) {
          setPresence({
            userId: snap.id,
            status: snap.data().status as AgentStatus,
            displayName: snap.data().displayName || "",
            role: snap.data().role || "",
            lastSeen: snap.data().lastSeen || "",
          });
        } else {
          setPresence({ userId, status: "offline" });
        }
      }
    );

    return () => unsubscribe();
  }, [userId]);

  return presence;
}
